import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FileText } from "lucide-react";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { useForm } from "react-hook-form";
import { pdf } from "../../../constants/applicationConstants";
import { showHideLoadingDialog, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { tokens } from "../../../theme";
import { uploadIVRDocument, IVRDocumentInfo, removeIVRDocument } from "../../../redux/slice/ivr/ivrDocumentSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { selectIsValueFoundInToken } from "../../../redux/slice/authSlice";
import { CAN_ACCESS_APPLICATION } from "../../../constants/applicationConstants";
import { useModalStyles } from "../../common/useModalStyles";

interface MedicalNoteAuditUploadPopupProps {
  open: boolean;
  onClose: () => void;
  patientId?: number;
}

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const MedicalNoteAuditUploadPopup: React.FC<MedicalNoteAuditUploadPopupProps> = ({
  open,
  onClose,
  patientId,
}) => {
  const {
    control,
    setValue,
    getValues,
    clearErrors,
    trigger,
    reset,
  } = useForm();
  const dispatch = useAppDispatch();
  // CS-1594 - Audit data management for uploaded documents
  const [auditDataByDocument, setAuditDataByDocument] = useState<Map<string, any>>(new Map());
  // Key to force Dropzone re-render and reset its internal state
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    cancelBtnSx,
  } = useModalStyles();
  const hasSubscribedAccessToMedicalAudit = useSelector((state: RootState) => 
    selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT)(state)
  );

  // Reset form and state when popup opens
  useEffect(() => {
    if (open) {
      reset();
      setAuditDataByDocument(new Map());
      setDropzoneKey(prev => prev + 1);
    }
  }, [open, reset]);

  const removeDataUriPrefix = (dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  };

  const handleDropzoneChange = async (uploadedFiles: ExtendedFile[]) => {
    const newFiles = uploadedFiles.filter((file) => !file.documentId);
    
    if (newFiles.length === 0) {
      return;
    }

    const medicalNoteDocIds = getValues('medicalNoteDocument') || [];
    
    try {
      dispatch(showHideLoadingDialog(true));
      clearErrors("medicalNoteDocument");
      
      for (const file of newFiles) {
        const base64Content = await readFileAsBase64(file);
        
        const documentObject: IVRDocumentInfo = {
          ivrId: null,
          documentName: file.name,
          content: removeDataUriPrefix(base64Content),
          type: "IVR",
          fileType: file.type,
        };
        
        const response = await dispatch(uploadIVRDocument(documentObject));
        
        if (response.payload.data && response.payload.data.documentId) {
          const documentId = response.payload.data.documentId;
          // CS-1594 - Store documentId as string for consistency with Map keys
          file.documentId = String(documentId);
          medicalNoteDocIds.push(documentId);
        }
      }
      
      setValue("medicalNoteDocument", medicalNoteDocIds);
      trigger("medicalNoteDocument");
    } catch (error) {
      console.error("Error uploading document:", error);
      dispatch(setSingleAlertObj({ 
        message: "Failed to upload document.", 
        alertType: "error" 
      }));
    } finally {
      dispatch(showHideLoadingDialog(false));
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!documentId) {
      return;
    }
    
    try {
      const response = await dispatch(removeIVRDocument(documentId.toString()));
      if (response.payload.code === "V3OK") {
        let medicalNoteDocIds = getValues('medicalNoteDocument') || [];
        medicalNoteDocIds = medicalNoteDocIds.filter((id: number) => String(id) !== String(documentId));
        setValue("medicalNoteDocument", medicalNoteDocIds);
        
        // CS-1594 - Clean up audit data when file is deleted
        setAuditDataByDocument(prev => {
          const newMap = new Map(prev);
          newMap.delete(String(documentId));
          return newMap;
        });
        
        if (medicalNoteDocIds.length === 0) {
          trigger("medicalNoteDocument");
        }
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      dispatch(setSingleAlertObj({ 
        message: "Failed to delete document.", 
        alertType: "error" 
      }));
    }
  };

  // CS-1594 - Handler for when audit completes
  const handleAuditComplete = (documentId: string, auditData: any) => {
    console.log("CS-1594: Audit completed for document", documentId);
    setAuditDataByDocument(prev => {
      const newMap = new Map(prev);
      newMap.set(documentId, auditData);
      return newMap;
    });
  };

  // CS-XXXX - Handler to close all popups when Medical Note Audit popup close button is clicked
  // This function will be passed to the Dropzone component, which will pass it to MedicalNoteAuditInlineButton,
  // which will pass it to MedicalNoteAuditPopup. When the close button in the audit popup is clicked,
  // it will close both the audit popup and this upload popup.
  const handleCloseAllPopups = () => {
    console.log("CS-XXXX: Closing all popups (audit popup + upload popup)");
    handleClose();
  };

  const handleClose = async () => {
    const medicalNoteDocIds = getValues("medicalNoteDocument") || [];
    
    // Delete all uploaded documents from backend
    if (medicalNoteDocIds && medicalNoteDocIds.length > 0) {
      const removePromises = medicalNoteDocIds.map((id: number) =>
        dispatch(removeIVRDocument(id.toString()))
      );
      await Promise.all(removePromises);
    }
    
    onClose();
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={handleClose}
      aria-describedby="medical-note-audit-upload-dialog"
      onClick={(event) => {
        event.stopPropagation();
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(modalTokens.accent)}>
          <FileText size={20} color={modalTokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ flex: 1, pr: 4 }}>
          <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: modalTokens.textPrimary, lineHeight: 1.3 }}>
            Upload Medical Note for Audit
          </Typography>
          <Typography id="medical-note-audit-upload-dialog" sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
            Upload one PDF medical note and run audit from the uploaded file row.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={bodySx}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 1, width: "100%" }}>
              <Grid container spacing={2}>
                <Grid item md xs={12}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.warning[300],
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    Upload a PDF medical note document. After upload, click the "Run Medical Note Audit" button next to the document.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            {/* CS-1706 START - Use maxFileAllowed to restrict single file upload at browser level */}
            <Dropzone
              key={dropzoneKey}
              name="medicalNoteDocument"
              onFileChange={() => {}}
              onFileChangeTest={handleDropzoneChange}
              control={control}
              rules={{ required: "Medical note document is required." }}
              maxFileSize={{ size: 10, unit: "MB" }}
              maxFileAllowed={1}
              onDeleteFile={handleDeleteDocument}
              allowedFileTypes={{
                ...pdf,
              }}
              showMedicalAuditButton={hasSubscribedAccessToMedicalAudit}
              patientId={patientId}
              docType="medical notes"
              auditDataByDocument={auditDataByDocument}
              onAuditComplete={handleAuditComplete}
              onCloseAllPopups={handleCloseAllPopups}
            />
            {/* CS-1706 END */}
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Supported format: PDF only (Maximum 1 file)
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={footerSx}>
        <Grid container justifyContent="flex-end" spacing={1}>
          <Grid item xs={12} sm="auto">
            <Box
              component="button"
              type="button"
              onClick={handleClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Cancel
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default MedicalNoteAuditUploadPopup;
